import { type DeltaTone, deltaTone } from '@/components/analytics/viz/format-delta';
import {
  type CampaignMeasure,
  type CountedKind,
  MEASURE_WORD,
  NO_TARGET_SET_NOTE,
  type WaitlistMeasure,
  isThinOutcome,
  measureCampaign,
  measureWaitlist,
  mismatchedCountNote,
  outcomeAcceptsCount,
  unclassifiedCountNote,
} from '@/lib/campaigns/campaign-measure';
import {
  type EvidenceChannel,
  type EvidenceInput,
  FLOOR_CONFIG,
  type GateCode,
  type Judgement,
  NO_TARGET_COPY,
  type ProviderLearningState,
  type StopKind,
  judgeCampaign,
} from '@/lib/campaigns/evidence-floors';
import type { PaidSpendTrendPoint } from '@/lib/campaigns/paid-campaign-table';
import { formatCentAmount, formatWholeAmount } from '@/lib/campaigns/paid-money';
import { type GoogleReadsPurchases, notReadReason } from '@/lib/paid-media/google/purchase-read';
import type { PlanOutcome } from '@/lib/paid-plan/offer';

/**
 * What a campaign's detail page says about the campaign (one-family sheet, frame 5): one sentence as
 * the hero, four figures each with its change on the previous window and its source, and one suggested
 * move behind a confirmation. Everything here is pure; the page hands in the daily series and the
 * facts it has read.
 *
 * Evidence by default (board ruling EVIDENCE-FLOORS): the sentence states what was reported, and the
 * words of a judgement ("too few to judge yet", a move) appear only when `judgeCampaign` lets them.
 * No floor value is ever printed; the copy carries the campaign's own figures. Unknown is never
 * zero: a figure that was not read is `null` and is worded "Not read".
 */

export type DetailProvider = 'meta-ads' | 'google-ads' | 'linkedin-ads';

export const PROVIDER_NAME: Readonly<Record<DetailProvider, string>> = {
  'meta-ads': 'Meta',
  'google-ads': 'Google Ads',
  'linkedin-ads': 'LinkedIn',
};

/** What the series' result count is called: Meta's is its purchase action, the others' their conversions. */
export const RESULT_NOUN: Readonly<Record<DetailProvider, string>> = {
  'meta-ads': 'purchase',
  'google-ads': 'conversion',
  'linkedin-ads': 'conversion',
};

/**
 * The result noun for a provider, with Google's "conversion" becoming "purchase" only when the proof that
 * its conversions are website purchases holds (`googleReadsPurchases`). Never otherwise.
 */
export function resultNounFor(provider: DetailProvider, purchasesProven?: boolean): string {
  return provider === 'google-ads' && purchasesProven === true ? 'purchase' : RESULT_NOUN[provider];
}

function purchasesProvenOf(facts: Pick<EvidenceFacts, 'googlePurchases' | 'planMeasure'>): boolean {
  return facts.googlePurchases?.reads === true && facts.planMeasure?.outcome === 'purchases';
}

const CHANNEL: Readonly<Record<DetailProvider, EvidenceChannel>> = {
  'meta-ads': 'meta',
  'google-ads': 'google',
  'linkedin-ads': 'linkedin',
};

function plural(count: number, noun: string): string {
  return `${count.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${noun}${count === 1 ? '' : 's'}`;
}

export const NOT_READ = 'Not read';

/**
 * The count the campaign's own goal reports when it is not the purchase count the daily series
 * carries (a Leads or Traffic campaign on Meta). `count` is null when it was not read.
 */
export interface ObjectiveResult {
  /** Singular noun: "lead", "landing page view". */
  noun: string;
  count: number | null;
}

export const RESULTS_NOT_READ_FOR_GOAL = 'Results are not read for this goal';

/** What the person reads of the attribution window, from the Meta columns the sync stores. */
export interface AttributionFacts {
  settings: readonly string[];
  windows: readonly string[];
}

const ATTRIBUTION_WINDOW = /^(\d+)d_(click|view)$/i;

function attributionWords(facts: AttributionFacts | null | undefined): string[] {
  const words: string[] = [];
  for (const raw of [...(facts?.windows ?? []), ...(facts?.settings ?? [])]) {
    const match = ATTRIBUTION_WINDOW.exec(raw.trim());
    if (!match) continue;
    const word = `${match[1]}-day ${match[2].toLowerCase()}`;
    if (!words.includes(word)) words.push(word);
  }
  return words;
}

/**
 * "using 7-day click or 1-day view", or "attribution window not read" when Meta stored none; empty for
 * the channels that state no window.
 */
export function attributionClause(
  provider: DetailProvider,
  facts: AttributionFacts | null | undefined,
): string {
  if (provider !== 'meta-ads') return '';
  const words = attributionWords(facts);
  return words.length > 0 ? `using ${words.join(' or ')}` : 'attribution window not read';
}

/** Whether a window holds enough results of the measure to read a cost from. Compared, never printed. */
export function enoughResults(results: number | null | undefined): boolean {
  return typeof results === 'number' && results >= FLOOR_CONFIG.verdictResults.value;
}

// ---------------------------------------------------------------------------------------------
// Windows
// ---------------------------------------------------------------------------------------------

export interface WindowFigures {
  /** Spend in minor units; null when no day reported one. */
  spendMinor: number | null;
  results: number | null;
  /** Provider-attributed conversion value, in the account currency's major unit. */
  valueMajor: number | null;
  /** Days on which spend was recorded. */
  deliveryDays: number;
}

function sumKnown(values: readonly (number | null | undefined)[]): number | null {
  const known = values.filter((value): value is number => typeof value === 'number');
  return known.length === 0 ? null : known.reduce((sum, value) => sum + value, 0);
}

/** The totals of one window of the daily series. Null when the series has no day at all. */
export function windowFigures(
  points: readonly PaidSpendTrendPoint[] | null | undefined,
): WindowFigures | null {
  if (!points || points.length === 0) return null;
  return {
    spendMinor: sumKnown(points.map((point) => point.spendMinor)),
    results: sumKnown(points.map((point) => point.conversions)),
    valueMajor: sumKnown(points.map((point) => point.conversionValueMajor)),
    deliveryDays: points.filter((point) => (point.spendMinor ?? 0) > 0).length,
  };
}

function spendMajor(figures: WindowFigures | null): number | null {
  if (figures === null || figures.spendMinor === null) return null;
  return figures.spendMinor / 100;
}

function costOf(figures: WindowFigures | null): number | null {
  const spend = spendMajor(figures);
  if (spend === null || figures === null || figures.results === null || figures.results <= 0) {
    return null;
  }
  return spend / figures.results;
}

// ---------------------------------------------------------------------------------------------
// Tiles
// ---------------------------------------------------------------------------------------------

export interface TileChange {
  /** What the tile prints: "8.4% more", "7.5% lower", "No change". */
  text: string;
  direction: 'up' | 'down' | 'flat';
  /** Good or bad for THIS figure (a lower cost is good); spend is never toned. */
  tone: DeltaTone;
  /** The full sentence, for assistive tech and the tooltip. */
  label: string;
}

export type TileKey = 'spent' | 'results' | 'cost' | 'value';

export interface FigureTile {
  key: TileKey;
  label: string;
  value: string;
  change: TileChange | null;
  /** Said under the figure when it has no change to show: why. */
  note: string | null;
}

export interface TileSet {
  tiles: FigureTile[];
  /** `read` when both windows delivered, so every tile shows its own change; `not_read` and no tile shows one. */
  comparison: 'read' | 'not_read';
  comparisonNote: string | null;
  /** Both windows were read, but one holds too few results to say which way the cost moved. */
  tooFewToCompare: boolean;
}

export function changeOf(
  current: number | null,
  prior: number | null,
  higherIsBetter: boolean | null,
  windowDays: number,
  noun: string,
): TileChange | null {
  if (current === null || prior === null || !Number.isFinite(current) || !Number.isFinite(prior)) {
    return null;
  }
  if (prior <= 0 || current < 0) return null;
  const pct = ((current - prior) / prior) * 100;
  if (Math.abs(pct) < 0.05) {
    return {
      text: 'No change',
      direction: 'flat',
      tone: 'neutral',
      label: `${noun} is the same as the previous ${windowDays} days.`,
    };
  }
  const up = pct > 0;
  const direction = up ? 'up' : 'down';
  const shown = Math.abs(pct).toFixed(1);
  return {
    text: `${shown}% ${up ? 'more' : 'lower'}`,
    direction,
    tone: deltaTone(direction, higherIsBetter),
    label: `${noun} is ${shown}% ${up ? 'higher' : 'lower'} than the previous ${windowDays} days.`,
  };
}

export const NOT_ENOUGH_TO_COMPARE = 'Not enough in the previous window to compare';
export const VALUE_FOR_PURCHASES_ONLY = 'Value is read for purchases only';

export function buildTiles(input: {
  provider: DetailProvider;
  currencyCode: string | null;
  current: WindowFigures | null;
  prior: WindowFigures | null;
  days: number;
  /** False when the daily series' count is not the campaign's own measure (a Leads or Traffic goal). */
  measureIsOwn?: boolean;
  /** The goal's own count, when the series' is not it. */
  objectiveResult?: ObjectiveResult | null;
  /** Google only: its conversions are proven website purchases, so the count is called purchases. */
  purchasesProven?: boolean;
}): TileSet {
  const { provider, currencyCode, current, prior, days } = input;
  const own = input.measureIsOwn ?? true;
  const objective = input.objectiveResult ?? null;
  const noun = own ? resultNounFor(provider, input.purchasesProven) : (objective?.noun ?? null);
  const by = PROVIDER_NAME[provider];
  const nouns = noun === null ? 'results' : `${noun}s`;
  const nounWord = noun ?? 'result';
  const delivered = Boolean(current && prior && current.deliveryDays > 0 && prior.deliveryDays > 0);
  // A previous window that delivered on fewer days than this one is a part of a window, not the window:
  // a change against it is the campaign's age, not a trend.
  const read = delivered && (prior?.deliveryDays ?? 0) >= (current?.deliveryDays ?? 0);

  const spend = spendMajor(current);
  const results = own ? (current?.results ?? null) : (objective?.count ?? null);
  const value = own ? (current?.valueMajor ?? null) : null;
  const cost = own
    ? costOf(current)
    : spend !== null && results !== null && results > 0
      ? spend / results
      : null;

  const enough = own && enoughResults(current?.results) && enoughResults(prior?.results);
  const tooFew = read && own && !enough;
  // Below the floor a change in volume is shown without a good or bad tone, and a cost has no direction.
  const resultsPolarity = enough ? true : null;

  // Below the floor the headline says there are too few to compare, so only the spend change prints a percentage.
  const changes =
    read && own && enough
      ? {
          spent: changeOf(spend, spendMajor(prior), null, days, 'Spend'),
          results: changeOf(
            results,
            prior?.results ?? null,
            resultsPolarity,
            days,
            `Reported ${nouns}`,
          ),
          cost: enough ? changeOf(cost, costOf(prior), false, days, `Cost per ${nounWord}`) : null,
          value: changeOf(
            value,
            prior?.valueMajor ?? null,
            resultsPolarity,
            days,
            `${capitalize(nounWord)} value`,
          ),
        }
      : {
          spent: read ? changeOf(spend, spendMajor(prior), null, days, 'Spend') : null,
          results: null,
          cost: null,
          value: null,
        };

  const note = (change: TileChange | null, floored = false): string | null =>
    read && change === null
      ? floored && tooFew
        ? `Too few ${nouns} to compare`
        : NOT_ENOUGH_TO_COMPARE
      : null;

  const resultsNote = !own
    ? noun === null || results === null
      ? RESULTS_NOT_READ_FOR_GOAL
      : null
    : note(changes.results, true);
  const costNote = !own
    ? results === null
      ? noun === null
        ? RESULTS_NOT_READ_FOR_GOAL
        : `${capitalize(nouns)} are not read`
      : cost === null
        ? `No ${nouns} were counted`
        : null
    : cost === null
      ? results === null
        ? `${capitalize(nouns)} are not read`
        : `No ${nouns} were counted`
      : tooFew
        ? 'No cost change shown'
        : note(changes.cost);

  const tiles: FigureTile[] = [
    {
      key: 'spent',
      label: 'Spent',
      value: spend === null ? NOT_READ : formatCentAmount(spend, currencyCode),
      change: changes.spent,
      note: note(changes.spent),
    },
    {
      key: 'results',
      label: noun === null ? `Results, ${by}` : `${capitalize(nouns)}, ${by}`,
      value:
        results === null
          ? NOT_READ
          : results.toLocaleString(undefined, { maximumFractionDigits: 1 }),
      change: changes.results,
      note: resultsNote,
    },
    {
      key: 'cost',
      label: `Cost per ${nounWord}, ${by}`,
      value: cost === null ? '—' : formatCentAmount(cost, currencyCode),
      change: changes.cost,
      note: costNote,
    },
    ...(own
      ? [
          {
            key: 'value' as const,
            label:
              provider === 'google-ads' && input.purchasesProven === true
                ? `Value you assigned, ${by}`
                : `${capitalize(nounWord)} value, ${by}`,
            value: value === null ? NOT_READ : formatCentAmount(value, currencyCode),
            change: changes.value,
            note:
              read && changes.value === null && tooFew
                ? 'No value change shown'
                : note(changes.value, true),
          },
        ]
      : []),
  ];

  const unreadReason =
    delivered && !read
      ? `The previous ${days} days delivered on fewer days than the last ${days}, so no change is shown.`
      : `The previous ${days} days are not read, so no change is shown.`;

  return {
    tiles,
    comparison: read ? 'read' : 'not_read',
    comparisonNote: read ? null : unreadReason,
    tooFewToCompare: tooFew,
  };
}

function capitalize(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export interface SourceParts {
  /** Who reported: "Meta". */
  by: string;
  /** The window, and the one it is set against when that was read: "15 to 21 Oct 2026, against 8 to 14 Oct". */
  window: string;
  /** "Read 22 Oct, 09:40", or that the time is not known. */
  read: string;
  /** What the counts are and are not, in one paragraph. */
  note: string;
  /** When the campaign last delivered ("Last delivered 29 Oct", or that nothing is synced); null when the page does not know. */
  delivery: string | null;
}

export const LATE_DAYS_NOTE = 'The latest days can still rise while Meta finishes counting.';

/**
 * The source line under the figures, in the parts the page draws: who reported, the window and what it
 * is set against, when it was read, and then one paragraph on what the counts are. The previous window
 * is named only when it was read. Only Meta gets the note about late days: it counts a purchase after the
 * click, so the newest days are undercounted until its window closes.
 */
export function sourceParts(input: {
  provider: DetailProvider;
  /** The window in words: "15 to 21 Oct 2026"; null when it is not known. */
  range: string | null;
  /** The window the figures are set against, in words ("8 to 14 Oct"), when both windows were read. */
  priorRange: string | null;
  days: number;
  readAt: string | null;
  measureIsOwn?: boolean;
  objectiveResult?: ObjectiveResult | null;
  attribution?: AttributionFacts | null;
  /** Says the provider's count is not read as the plan's result; replaces the sentence that says what the counts are. */
  countNote?: string | null;
  /** Google only: its conversions are proven website purchases, so the count is called purchases. */
  purchasesProven?: boolean;
  /** A store (Shopify) is connected to the workspace, so "the store's own orders are not read" is a true thing to say. */
  storeConnected?: boolean;
  /** The last day the campaign delivered, already worded ("29 Oct 2026"); null when nothing is synced; undefined when not known. */
  lastDelivered?: string | null;
}): SourceParts {
  const by = PROVIDER_NAME[input.provider];
  const own = input.measureIsOwn ?? true;
  const noun = own
    ? resultNounFor(input.provider, input.purchasesProven)
    : (input.objectiveResult?.noun ?? null);
  const range = input.range ?? `the last ${input.days} days`;
  const against = input.priorRange ? `, against ${input.priorRange}` : '';
  const read = input.readAt ? `Read ${input.readAt}` : 'Read time not known';
  // The store line is for a channel that sells to a store: Meta or Google, and only when the goal is
  // purchases or a store is connected. A LinkedIn campaign for a services firm has no store to name.
  const sellsToStore =
    input.provider !== 'linkedin-ads' && (noun === 'purchase' || input.storeConnected === true);
  const tail = sellsToStore ? ' The store’s own orders are not read.' : '';
  const delivery =
    input.lastDelivered === undefined
      ? null
      : input.lastDelivered === null
        ? NO_DELIVERY_SYNCED
        : `Last delivered ${input.lastDelivered}`;
  const late = input.provider === 'meta-ads' ? ` ${LATE_DAYS_NOTE}` : '';
  if (noun === null) {
    return {
      by,
      window: `${range}${against}`,
      read,
      note: `Spend is as ${by} reports it. ${RESULTS_NOT_READ_FOR_GOAL}.${tail}${late}`,
      delivery,
    };
  }
  const words = input.provider === 'meta-ads' ? attributionWords(input.attribution) : [];
  const using =
    input.provider !== 'meta-ads'
      ? ''
      : words.length > 0
        ? `, using ${words.join(' or ')}`
        : ', attribution window not read';
  const article =
    noun === 'order' ? 'an order' : noun === 'conversion' ? 'a conversion' : `a ${noun}`;
  const values = own ? ` and ${noun} value` : '';
  const counts = input.countNote
    ? input.countNote
    : `${capitalize(noun)}s${values} are as ${by} reports them${using}, and ${by} counts ${article} it attributes to the ad.`;
  return { by, window: `${range}${against}`, read, note: `${counts}${tail}${late}`, delivery };
}

export const NO_DELIVERY_SYNCED = 'No delivery synced yet';

/**
 * The one caption under the figures. Normally "Reported by X for the window", what the counts are, the
 * comparison, when it last delivered and when it was read. When no tile holds a value there is nothing
 * reported to attribute, so the caption says that and no more: it does not say what spend "is as X reports
 * it" over tiles that read "Not read".
 */
export function sourceCaption(
  tiles: Pick<TileSet, 'tiles' | 'comparisonNote'>,
  source: SourceParts,
): { lead: string; rest: string } {
  const nothingRead = tiles.tiles.every((tile) => tile.value === NOT_READ || tile.value === '—');
  if (nothingRead) {
    return {
      lead: '',
      rest: `Nothing read from ${source.by} for ${source.window}. ${source.read}.`,
    };
  }
  const parts = [
    `for ${source.window}.`,
    source.note,
    tiles.comparisonNote,
    source.delivery ? `${source.delivery}.` : null,
    `${source.read}.`,
  ].filter((part): part is string => Boolean(part));
  return { lead: `Reported by ${source.by}`, rest: ` ${parts.join(' ')}` };
}

// ---------------------------------------------------------------------------------------------
// Judgement input
// ---------------------------------------------------------------------------------------------

/** Days since the latest dated entry in the campaign's record, or null when it has none. */
export function daysSinceLastRecordEntry(
  history: readonly { at: string; timeKnown: boolean }[],
  nowMs: number,
): number | null {
  let latest: number | null = null;
  for (const entry of history) {
    if (!entry.timeKnown) continue;
    const at = Date.parse(entry.at);
    if (Number.isFinite(at) && (latest === null || at > latest)) latest = at;
  }
  return latest === null ? null : Math.max(0, Math.floor((nowMs - latest) / 86_400_000));
}

export interface EvidenceFacts {
  provider: DetailProvider;
  currencyCode: string | null;
  current: WindowFigures | null;
  /** The campaign's own measure is the count the series carries (a sales objective on Meta, any on Google). */
  measureIsOwn: boolean;
  /** The goal's own count when the series' is not it; null when it was not read. */
  objectiveResult?: ObjectiveResult | null;
  createdByMora: boolean;
  history: readonly { at: string; timeKnown: boolean }[];
  nowMs: number;
  /** Provider budget a day in minor units, when the provider says one. */
  dailyBudgetMinor: number | null;
  /**
   * Set only for a campaign Mora created from a plan: the plan's result and the person's target cost per
   * result in the major unit (null when none is set). Absent, the campaign shows evidence only.
   */
  planMeasure?: { outcome: string; target: number | null } | null;
  /**
   * Waitlist plans only: the sign-ups the site recorded under the plan's tag. Null when the count was
   * not read; an unread count is never zero.
   */
  waitlistSignups?: number | null;
  /** Waitlist plans only: team sign-ups left out of that count. Null when not read. */
  waitlistExcluded?: number | null;
  /** What the campaign was made for, in one sentence (`purposeSentence`); null for a campaign with no plan. */
  purpose?: string | null;
  /** Set when the campaign is saved and has not been switched on, so nothing has run to measure. */
  notRun?: NotRunStage | null;
  /** The dates a synced campaign was set to run, when the provider stored them. */
  campaignSpan?: CampaignSpan | null;
  /** The window before `current`, for the cost the page compares against; null when it was not read. */
  prior?: WindowFigures | null;
  /** Whether the channel is still learning, from the provider's own read; null when it was not read. */
  providerState?: ProviderLearningState;
  /** Whether the budget limits delivery, from the provider's own read; null when it was not read. */
  budgetLimited?: boolean | null;
  /** The saved settings that differ from the request, in one line, when the stored read-back names them; null otherwise. */
  checkDifference?: string | null;
  /** Whether a plan sits behind the campaign, so the page may name it. */
  hasPlan?: boolean;
  /**
   * True when the provider's schedule or status says the campaign is over (lifecycle `ended`). A campaign
   * that ended short of its floor will never reach it, so Mora does not say it is waiting.
   */
  ended?: boolean;
  /** Set by the daily review when the last sync or the newest daily figure is too old to judge on; absent on the page. */
  stale?: StaleData | null;
  /** True when the provider reports the campaign paused (lifecycle `paused`), so there is nothing to stop. */
  paused?: boolean;
  /**
   * Google only: whether its conversions are read as website purchases (`googleReadsPurchases`). Absent
   * for other channels and when the page did not ask; then Google's count stays an unlabelled conversion.
   */
  googlePurchases?: GoogleReadsPurchases | null;
}

/**
 * The campaign was last read from the provider on `through` (YYYY-MM-DD), `FLOOR_CONFIG.staleDataDays` or more days
 * before the review, so its status and figures may have changed since. Set by the daily review, never by the detail
 * page. Old daily rows with a fresh read are not stale: a day without delivery stores no row.
 */
export interface StaleData {
  /** YYYY-MM-DD. */
  through: string;
}

function dayLabel(day: string): string {
  return new Date(`${day}T00:00:00.000Z`).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  });
}

export const STALE_DATA_HEADLINE = 'These figures are out of date.';
export const STALE_DATA_HEADING = 'Figures are out of date';

export function staleDataSupport(provider: DetailProvider, stale: StaleData): string {
  const by = PROVIDER_NAME[provider];
  return `Mora last read this campaign from ${by} on ${dayLabel(stale.through)}, so its status and figures may have changed since. It may still be spending. Check it in ${by}. Mora proposes nothing new until it reads it again.`;
}

export type NotRunStage =
  | 'created_paused'
  | 'created_as_draft'
  | 'needs_a_check'
  /** A synced campaign Mora did not create, whose whole span Mora's stored history covers and holds no delivery. */
  | 'no_delivery'
  /** A synced campaign Mora did not create, whose dates Mora's stored history does not cover: no read, not none. */
  | 'no_read';

export const NOT_RUN_HEADLINE = 'This campaign has not run yet.';

/** The dates a synced campaign was set to run, as ISO days; the stop is null while it has none. */
export interface CampaignSpan {
  start: string | null;
  stop: string | null;
  /** The stop is null or not yet past. */
  running: boolean;
}

function spanDay(day: string): string {
  const date = new Date(`${day}T00:00:00.000Z`);
  if (Number.isNaN(date.getTime())) return day;
  return date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * The headline of a campaign with nothing to measure. "Has not run yet" is said only of a campaign that
 * can still run: a stop date in the past makes it "never delivered", and a span Mora holds no history for
 * is "no delivery read", never either claim.
 */
export function notRunHeadline(stage: NotRunStage, span?: CampaignSpan | null): string {
  if (stage === 'no_read') {
    if (span?.start && span.stop && !span.running) {
      return `Mora has no delivery read for ${spanDay(span.start)} to ${spanDay(span.stop)}.`;
    }
    if (span?.start) return `Mora has no delivery read since ${spanDay(span.start)}.`;
    return 'Mora has no delivery read for this campaign.';
  }
  if (stage === 'no_delivery' && span && !span.running) return 'This campaign never delivered.';
  return NOT_RUN_HEADLINE;
}

/**
 * The judgement's inputs from what the page has read. Three things are not read anywhere yet, and each
 * is therefore `null`, which no gate passes: whether the provider is still learning, the person's
 * target for a result, and whether the budget is the limit. Tracking counts as confirmed only when a
 * result was actually counted in the window.
 */
/**
 * Whether the cost per result fell against the window before. Null unless both windows delivered alike
 * and counted something, because a part-window or an unread one says nothing about a trend.
 */
export function trendImprovingOf(
  facts: Pick<EvidenceFacts, 'current' | 'prior' | 'measureIsOwn'>,
): boolean | null {
  if (!facts.measureIsOwn) return null;
  const { current, prior } = facts;
  if (
    !current ||
    !prior ||
    current.deliveryDays === 0 ||
    prior.deliveryDays < current.deliveryDays
  ) {
    return null;
  }
  const now = costOf(current);
  const before = costOf(prior);
  if (now === null || before === null) return null;
  return now < before;
}

export function buildEvidenceInput(facts: EvidenceFacts): EvidenceInput {
  const { current } = facts;
  // A waitlist plan's result is the site's own sign-up count, which Mora reads itself: it is the campaign's
  // own measure whatever objective the channel runs, and a count that was read is a count that is kept.
  const waitlistRead =
    facts.planMeasure?.outcome === 'waitlist' && typeof facts.waitlistSignups === 'number';
  const results = waitlistRead
    ? (facts.waitlistSignups as number)
    : facts.measureIsOwn
      ? (current?.results ?? null)
      : (facts.objectiveResult?.count ?? null);
  return {
    channel: CHANNEL[facts.provider],
    measureClass: 'standard',
    createdByMora: facts.createdByMora,
    figuresAreOwnMeasure: waitlistRead || facts.measureIsOwn,
    trackingConfirmed: waitlistRead || (results !== null && results > 0) ? true : null,
    providerState: facts.providerState ?? null,
    daysSinceLastEdit: facts.createdByMora
      ? daysSinceLastRecordEntry(facts.history, facts.nowMs)
      : null,
    results,
    spend: spendMajor(current),
    daysDelivered: current ? current.deliveryDays : null,
    target: facts.planMeasure?.target ?? null,
    budgetLimited: facts.budgetLimited ?? null,
    dailyAmount: facts.dailyBudgetMinor === null ? null : facts.dailyBudgetMinor / 100,
    daysSinceLastRaise: null,
    trendImproving: trendImprovingOf(facts),
    currency: facts.currencyCode ?? 'USD',
    measureNoun: waitlistRead
      ? 'waitlist sign-up'
      : facts.measureIsOwn
        ? resultNounFor(facts.provider, purchasesProvenOf(facts))
        : (facts.objectiveResult?.noun ?? 'result'),
  };
}

/**
 * Said on the support line of a campaign Mora did not create when the move section below it already says
 * what would decide the cost, so the page does not answer "No move yet" and then "Your call" in a row.
 */
export const NOT_CREATED_NO_TARGET =
  'Mora did not create or turn on this campaign, so it has no target to hold the cost against.';

const NO_CHANGE_PREFIX = /^No (?:change|move) yet\.\s*/;

/** Drops the heading words a sentence repeats when it sits under a heading that already says them. */
export function withoutNoChangePrefix(text: string): string {
  return text.replace(NO_CHANGE_PREFIX, '');
}

/**
 * The sentence for a gate that held judgement back. A gate that failed because its fact was never read
 * says so in those words, not as if the fact were known to be bad.
 */
export function gateSentence(judgement: Judgement, input: EvidenceInput): string {
  const code: GateCode | null = judgement.gate;
  if (code === 'provider_learning' && input.providerState === null) {
    return 'No move yet. Mora has not read whether the channel is still learning, so it is not judging this campaign.';
  }
  if (code === 'recent_edit' && input.daysSinceLastEdit === null) {
    return 'No move yet. Mora has not read when this campaign was last changed, so it is not judging it.';
  }
  if (code === 'tracking_unconfirmed' && input.results === null) {
    return 'No move yet. Mora has not read the result count for this campaign, so it cannot judge it.';
  }
  return judgement.basis;
}

// ---------------------------------------------------------------------------------------------
// The sentence
// ---------------------------------------------------------------------------------------------

export interface DetailVerdict {
  headline: string;
  support: string;
  judgement: Judgement;
  /** What the judgement was made from, kept so the suggested move reads the same facts. */
  evidence: EvidenceInput;
  /** The plan-based measure (step 5); null for a campaign with no plan or nothing read. */
  measure?: CampaignMeasure | WaitlistMeasure | null;
  /** What the campaign was made for; shown under the sentence. Null when it has no plan. */
  purpose?: string | null;
  /** The campaign is saved and switched off: nothing has been spent, so nothing is measured. */
  notRun?: NotRunStage | null;
}

/** What a saved, switched-off campaign says in place of figures. Nothing is spent, so nothing is claimed. */
export function notRunSupport(provider: DetailProvider, stage: NotRunStage): string {
  const by = PROVIDER_NAME[provider];
  if (stage === 'no_delivery') {
    return `Mora read this campaign and ${by} has delivered nothing for it, so there is nothing to measure.`;
  }
  if (stage === 'no_read') {
    return `Mora stored no daily figures for those dates, so it cannot say whether ${by} delivered anything.`;
  }
  if (stage === 'needs_a_check') {
    return `${by} has not delivered it, so there is nothing to measure yet.`;
  }
  return stage === 'created_as_draft'
    ? `It is a draft in ${by} that Mora has not turned on, so there is nothing to measure yet.`
    : 'Mora saved it switched off and has not turned it on, so there is nothing to measure yet.';
}

/** The sentence under the check's lead fact; it states what Mora does about it and uses neither verb of the lead. */
export function needsACheckReason(): string {
  return 'Mora does not turn it on until the saved settings are confirmed.';
}

/** The lead fact of the check when the stored read-back names no difference. */
export function needsACheckLead(provider: DetailProvider, hasPlan = false): string {
  return `Mora could not confirm that ${PROVIDER_NAME[provider]} holds this campaign as ${hasPlan ? 'planned' : 'requested'}`;
}

/** The measure of a plan campaign from the facts the page has read, or null when it has none. */
export function measureOfFacts(
  facts: EvidenceFacts,
  days: number,
): CampaignMeasure | WaitlistMeasure | null {
  const plan = facts.planMeasure ?? null;
  if (!plan || !facts.createdByMora) return null;
  // A waitlist is counted from the site's own sign-ups, never from the channel's count, so it skips
  // the channel gates below and carries its own limitation.
  if (plan.outcome === 'waitlist') {
    const spendMinor = facts.current?.spendMinor ?? null;
    return measureWaitlist({
      signups: facts.waitlistSignups ?? null,
      excluded: facts.waitlistExcluded ?? null,
      spend: spendMinor === null ? null : spendMinor / 100,
      daysDelivered: facts.current ? facts.current.deliveryDays : null,
      windowDays: days,
      currencyCode: facts.currencyCode,
      target: plan.target,
      madeFromPlan: true,
    });
  }
  // Only the campaign's own measure is the plan's result; a goal's other count is evidence, not a verdict.
  if (!facts.measureIsOwn) return null;
  const input = buildEvidenceInput(facts);
  const judgement = judgeCampaign(input);
  // A recent change holds every judgement back; its own sentence says why, not a result count.
  if (
    judgement.gate === 'not_own_measure' ||
    judgement.gate === 'tracking_unconfirmed' ||
    judgement.gate === 'recent_edit'
  )
    return null;
  const counted: CountedKind =
    facts.provider === 'meta-ads' || purchasesProvenOf(facts)
      ? 'purchase'
      : 'conversion_unclassified';
  const measure = measureCampaign({
    madeFromPlan: true,
    outcome: plan.outcome,
    counted,
    channel: input.channel,
    providerName: PROVIDER_NAME[facts.provider],
    results: input.results,
    spend: input.spend,
    daysDelivered: input.daysDelivered,
    windowDays: days,
    target: plan.target,
    currencyCode: facts.currencyCode,
    noun: input.measureNoun ?? 'result',
    thin: isThinOutcome(plan.outcome),
  });
  if (!measure) return null;
  // Below the verdict count, a cost far over the person's own target is still a Stop (EVIDENCE-FLOORS), so the
  // measure says Not working with the campaign's own figures instead of "too early".
  if (measure.state === 'too_early' && judgement.stop.eligible && judgement.stop.kind === 'stop') {
    const noun = input.measureNoun ?? 'result';
    const cost = measure.costPerResult;
    const line = `${MEASURE_WORD.above_target}: ${cost === null ? `no ${noun}s counted` : `${formatCentAmount(cost, facts.currencyCode)} a ${noun}`}`;
    const spent = input.spend === null ? null : formatWholeAmount(input.spend, facts.currencyCode);
    return {
      state: 'above_target',
      word: MEASURE_WORD.above_target,
      tone: 'warning',
      headline: `${line}, against your ${formatCentAmount(plan.target ?? 0, facts.currencyCode)} target.`,
      support: `${PROVIDER_NAME[facts.provider]} reports ${plural(input.results ?? 0, noun)}${spent ? ` for ${spent}` : ''} over ${plural(input.daysDelivered ?? 0, 'day')}.`,
      listLine: line,
      costPerResult: cost,
    };
  }
  // A direction is shown only when every evidence gate holds; otherwise the figures stand without it.
  const directional =
    measure.state === 'above_target' ||
    measure.state === 'on_target' ||
    measure.state === 'below_target';
  if (directional && judgement.state !== 'verdict') return null;
  return measure;
}

function headlineOf(input: {
  provider: DetailProvider;
  days: number;
  currencyCode: string | null;
  current: WindowFigures | null;
  tiles: TileSet;
  measureIsOwn: boolean;
  objectiveResult: ObjectiveResult | null;
  purchasesProven?: boolean;
}): string {
  const { provider, days, currencyCode, current, tiles } = input;
  const own = input.measureIsOwn;
  const noun = own
    ? resultNounFor(provider, input.purchasesProven)
    : (input.objectiveResult?.noun ?? null);
  // A direction is stated only when the tile earned one: both windows delivered alike and held enough results.
  const cost = own ? tiles.tiles.find((tile) => tile.key === 'cost') : undefined;
  if (cost?.change && noun !== null) {
    if (cost.change.direction === 'flat') {
      return `Cost per ${noun} is the same as the previous ${days} days.`;
    }
    return `Cost per ${noun} is ${cost.change.direction === 'down' ? 'down' : 'up'} on the previous ${days} days.`;
  }
  const spend = spendMajor(current);
  const results = own ? (current?.results ?? null) : (input.objectiveResult?.count ?? null);
  const spent = spend === null ? null : formatCentAmount(spend, currencyCode);
  if (spent !== null && spend !== null && spend > 0) {
    if (noun === null)
      return `${spent} spent in the last ${days} days. ${RESULTS_NOT_READ_FOR_GOAL}.`;
    if (results !== null && results > 0) {
      return `${plural(results, noun)} for ${spent} in the last ${days} days.`;
    }
    if (results === 0) return `${spent} spent in the last ${days} days, with no ${noun}s counted.`;
    return `${spent} spent in the last ${days} days. ${capitalize(noun)}s are not read.`;
  }
  if (current === null) return `Nothing is read for the last ${days} days yet.`;
  return `Nothing delivered in the last ${days} days.`;
}

/**
 * Says so when the provider's count is not the plan's result. Meta's series counts purchases and Google's
 * counts conversions without a kind; neither is read as sign-ups, bookings or purchases it does not
 * match. LinkedIn has no basis for the claim either way, so it gets no note. The page prints it under the
 * figures, where the source is said, and not a second time under the headline.
 */
export function countNote(
  facts: Pick<
    EvidenceFacts,
    'planMeasure' | 'createdByMora' | 'measureIsOwn' | 'current' | 'provider' | 'googlePurchases'
  >,
): string | null {
  const plan = facts.planMeasure ?? null;
  if (
    !plan ||
    plan.outcome === 'waitlist' ||
    !facts.createdByMora ||
    !facts.measureIsOwn ||
    facts.current?.results == null
  ) {
    return null;
  }
  const outcome = plan.outcome as PlanOutcome;
  if (facts.provider === 'google-ads') {
    const proof = facts.googlePurchases ?? null;
    if (proof && outcome === 'purchases') {
      return proof.reads
        ? null
        : mismatchedCountNote(
            PROVIDER_NAME[facts.provider],
            'purchases',
            outcome,
            notReadReason(proof),
          );
    }
    return unclassifiedCountNote(PROVIDER_NAME[facts.provider], outcome);
  }
  if (facts.provider === 'meta-ads' && !outcomeAcceptsCount(plan.outcome, 'purchase')) {
    return mismatchedCountNote(PROVIDER_NAME[facts.provider], 'purchases', outcome);
  }
  return null;
}

/**
 * The line under a direction: what the cost was made from, and what it was the window before. The
 * provider's name is already in the sentence above it, so it is not said again. The earlier cost is
 * given only when both windows delivered alike and counted something.
 */
export function directionSupport(facts: EvidenceFacts, days: number, tiles: TileSet): string {
  const noun = resultNounFor(facts.provider, purchasesProvenOf(facts));
  const spend = spendMajor(facts.current);
  const results = facts.current?.results ?? null;
  const head =
    results !== null && spend !== null
      ? `${plural(results, noun)} for ${formatCentAmount(spend, facts.currencyCode)} in the last ${days} days.`
      : '';
  const before = tiles.comparison === 'read' ? costOf(facts.prior ?? null) : null;
  const tail =
    before === null
      ? ''
      : ` The cost was ${formatCentAmount(before, facts.currencyCode)} the ${days} days before.`;
  return `${head}${tail}`.trim();
}

export function buildVerdict(input: {
  facts: EvidenceFacts;
  days: number;
  tiles: TileSet;
}): DetailVerdict {
  const evidenceInput = buildEvidenceInput(input.facts);
  const judgement = judgeCampaign(evidenceInput);
  const { provider } = input.facts;
  const purpose = input.facts.purpose ?? null;
  if (input.facts.stale) {
    return {
      headline: STALE_DATA_HEADLINE,
      support: staleDataSupport(provider, input.facts.stale),
      judgement,
      evidence: evidenceInput,
      measure: null,
      purpose,
    };
  }
  if (input.facts.notRun) {
    return {
      headline: notRunHeadline(input.facts.notRun, input.facts.campaignSpan),
      support: notRunSupport(provider, input.facts.notRun),
      judgement,
      evidence: evidenceInput,
      measure: null,
      purpose,
      notRun: input.facts.notRun,
    };
  }
  let headline = headlineOf({
    provider,
    days: input.days,
    currencyCode: input.facts.currencyCode,
    current: input.facts.current,
    tiles: input.tiles,
    measureIsOwn: input.facts.measureIsOwn,
    objectiveResult: input.facts.objectiveResult ?? null,
    purchasesProven: purchasesProvenOf(input.facts),
  });
  const measure = measureOfFacts(input.facts, input.days);
  if (measure) {
    return {
      headline: measure.headline,
      support: measure.sentence
        ? directionSupport(input.facts, input.days, input.tiles)
        : measure.support,
      judgement,
      evidence: evidenceInput,
      measure,
      purpose,
    };
  }
  let support: string;
  if (judgement.state === 'verdict') {
    support = `${PROVIDER_NAME[provider]} reports enough ${resultNounFor(provider, purchasesProvenOf(input.facts))}s in this window to read the cost.`;
  } else if (judgement.state === 'too_early') {
    support = judgement.basis;
  } else if (
    judgement.gate === 'not_created_by_mora' &&
    whatWouldDecideIt(evidenceInput, input.facts.currencyCode, {
      value: input.facts.current?.valueMajor ?? null,
      by: PROVIDER_NAME[provider],
    })
  ) {
    support = NOT_CREATED_NO_TARGET;
    const perResult = soFarCost(evidenceInput, input.facts.currencyCode);
    if (perResult) headline = perResult;
  } else {
    support = gateSentence(judgement, evidenceInput);
  }
  return { headline, support, judgement, evidence: evidenceInput, measure: null, purpose };
}

// ---------------------------------------------------------------------------------------------
// The one suggested move
// ---------------------------------------------------------------------------------------------

/** A candidate ad for the "Write a new opening line" move. */
export interface RewriteCandidate {
  key: string;
  name: string;
  /** What the move calls the ad: "line B" when its name ends in "opening line B", else its name. */
  label: string;
  cost: number | null;
  costText: string | null;
}

/** The ad the move starts with, the others beside it, and whether every one is over the target. */
export interface RewriteOffer {
  chosen: RewriteCandidate;
  others: RewriteCandidate[];
  allOver: boolean;
}

export type NextMove =
  | {
      kind: 'none';
      heading: string;
      reason: string;
      /** True when the reason is what the campaign still needs, so waiting is the whole answer. */
      waiting?: boolean;
      /** True when the move is a thing to do that the support line does not say, so it is never hidden as a repeat. */
      decision?: boolean;
      /** The saved settings need a check: the card carries its two actions and leads with the difference. */
      check?: boolean;
    }
  | {
      /** A saved, switched-off campaign: Turn on is the next move, or the reason it cannot be pressed. */
      kind: 'turn_on';
      title: string;
      basis: string;
      stage: NotRunStage;
      /** False when Turn on is not offered on this page, so the section says why instead of promising a button. */
      offered: boolean;
    }
  | {
      kind: 'raise';
      title: string;
      basis: string;
      /** Mora has no path that changes a provider amount, so the move is shown and cannot be pressed. */
      unavailableReason: string;
    }
  | {
      kind: 'rewrite';
      title: string;
      basis: string;
      sub: string;
      adKey: string;
      adName: string;
    }
  | {
      kind: 'stop';
      title: string;
      basis: string;
      stopKind: Exclude<StopKind, 'check_tracking'>;
      typedConfirmation: boolean;
      /** Stop is the first move; writing a new opening line is the second, when an ad can be written for. */
      rewrite: { adKey: string; adName: string } | null;
    };

/** The one heading for a page with nothing to press; the reason goes underneath. */
export const NO_MOVE_YET = 'No move yet';
export const DECIDE_ON_YOUR_NUMBER = 'What would decide it';
export const NO_CHANGE_YET = NO_MOVE_YET;
export const NOT_ENOUGH_DATA_YET = NO_MOVE_YET;

/**
 * What turning on does, said plainly: it starts spending, with the amount and dates when they are read,
 * and the result appears on this page once it has delivered. No reader is promised: Mora reads the
 * figures when the page is opened.
 */
export function turnOnBasis(
  provider: DetailProvider,
  stage: NotRunStage,
  outcome?: string | null,
  plannedSpend?: string | null,
  linkedInTurnOnOpen?: boolean,
): string {
  const by = PROVIDER_NAME[provider];
  if (stage === 'created_as_draft' && !linkedInTurnOnOpen) {
    return `Mora cannot turn on a LinkedIn draft. Turn it on in ${by}; the figures appear here once it delivers.`;
  }
  const days = isThinOutcome(outcome)
    ? FLOOR_CONFIG.thinVerdictDays.value
    : FLOOR_CONFIG.verdictDays.value;
  const spending = plannedSpend
    ? `Turning it on starts spending in ${by}, ${plannedSpend}.`
    : `Turning it on starts spending in ${by}.`;
  return `${spending} Once it has delivered for about ${days} days, the result appears here.`;
}

/**
 * Whether the suggested-move section would only say again what the line under the headline already says
 * (ruling CAMPAIGNS-BUILD-3 point 8). When the judgement is evidence only, the support line already reads
 * "No change yet. Mora did not create this campaign, so it shows the figures only", and a "No change yet"
 * heading with the same reason under it is the same sentence twice. The section is then left out, so the
 * sentence is said once.
 */
export function moveRepeatsSupport(
  verdict: Pick<DetailVerdict, 'judgement'>,
  move: NextMove,
): boolean {
  return (
    move.kind === 'none' &&
    move.waiting !== true &&
    move.decision !== true &&
    verdict.judgement.state === 'evidence_only'
  );
}

export function raiseUnavailableReason(provider: DetailProvider): string {
  return `Mora cannot change an amount yet. Change it in ${PROVIDER_NAME[provider]}; Mora will read the new amount.`;
}

export function buildNextMove(input: {
  verdict: DetailVerdict;
  facts: EvidenceFacts;
  /** Whether the page offers the Turn on button; false adds the reason in place of the promise. Defaults to true. */
  turnOnOffered?: boolean;
  /** The ad a new opening line would start with, when one can be written for; null when none can. */
  rewrite?: RewriteOffer | null;
  /** The figures of the window, for the change in cost the reasons mention. */
  tiles?: TileSet | null;
  /** The saved amount, length and dates in words ("$95 a day for 15 days (23 Oct to 6 Nov)"), when read. */
  plannedSpend?: string | null;
  /**
   * True when the account's `linkedin_status_transitions` row is on, so Mora can turn a LinkedIn draft on.
   * Closed (the default), a LinkedIn draft is turned on in LinkedIn and the page says so.
   */
  linkedInTurnOnOpen?: boolean;
}): NextMove {
  const { judgement } = input.verdict;
  const { provider, currencyCode } = input.facts;
  const by = PROVIDER_NAME[provider];

  if (input.facts.stale) {
    return {
      kind: 'none',
      heading: STALE_DATA_HEADING,
      reason: staleDataSupport(provider, input.facts.stale),
    };
  }

  if (input.verdict.notRun === 'needs_a_check') {
    const hasPlan = input.facts.hasPlan === true;
    return {
      kind: 'none',
      heading: input.facts.checkDifference ?? needsACheckLead(provider, hasPlan),
      reason: needsACheckReason(),
      decision: true,
      check: true,
    };
  }

  if (input.verdict.notRun === 'no_delivery') {
    return {
      kind: 'none',
      heading: NO_MOVE_YET,
      reason: `${by} has delivered nothing for this campaign, so there is nothing for Mora to judge.`,
    };
  }

  if (input.verdict.notRun === 'no_read') {
    return {
      kind: 'none',
      heading: NO_MOVE_YET,
      reason: `Mora has no delivery figures for this campaign, so there is nothing for it to judge.`,
    };
  }

  if (input.verdict.notRun) {
    const stage = input.verdict.notRun;
    const linkedInOpen = stage === 'created_as_draft' && input.linkedInTurnOnOpen === true;
    const offered =
      input.turnOnOffered !== false || (stage === 'created_as_draft' && !linkedInOpen);
    return {
      kind: 'turn_on',
      title:
        stage === 'created_as_draft' && !linkedInOpen
          ? 'Turn it on in LinkedIn'
          : offered
            ? 'Turn on'
            : 'Not turned on',
      basis: offered
        ? turnOnBasis(
            provider,
            stage,
            input.facts.planMeasure?.outcome,
            input.plannedSpend,
            linkedInOpen,
          )
        : 'It is saved and switched off.',
      stage,
      offered,
    };
  }

  // Stop does not wait for the verdict count: spend far over the person's own target is enough (EVIDENCE-FLOORS).
  // A campaign that is already off (paused by the person, or past its end) has nothing to stop. "Pause and
  // review" is offered while learning holds the judgement at evidence only; a full Stop needs a judgement.
  const off = input.facts.ended === true || input.facts.paused === true;
  if (
    !off &&
    (judgement.state !== 'evidence_only' || judgement.stop.kind === 'pause_and_review') &&
    judgement.stop.eligible &&
    judgement.stop.kind
  ) {
    if (judgement.stop.kind === 'check_tracking') {
      return { kind: 'none', heading: NO_MOVE_YET, reason: judgement.stop.copy };
    }
    const spend = input.verdict.evidence.spend;
    const value = input.facts.current?.valueMajor ?? null;
    const noun = RESULT_NOUN[provider];
    const returned =
      spend !== null && spend > 0 && value !== null
        ? `${by} reports ${formatCentAmount(value, currencyCode)} of ${noun} value, ${(value / spend).toFixed(1)} times what was spent.`
        : null;
    const basis = returned
      ? judgement.stop.copy.replace(
          / You can stop it or leave it\.$/,
          ` ${returned} You can stop it or leave it.`,
        )
      : judgement.stop.copy;
    return {
      kind: 'stop',
      title: judgement.stop.kind === 'pause_and_review' ? 'Pause and review' : 'Stop this campaign',
      basis,
      stopKind: judgement.stop.kind,
      typedConfirmation: judgement.stop.requiresTypedConfirmation,
      rewrite: input.rewrite
        ? { adKey: input.rewrite.chosen.key, adName: input.rewrite.chosen.name }
        : null,
    };
  }

  const measure = input.verdict.measure;
  const needs = measure?.state === 'too_early' ? measure.needs : null;
  if (needs && input.facts.ended === true) {
    return {
      kind: 'none',
      heading: 'Ended before it could be judged',
      reason: `${needs} The campaign has ended, so it will not get there. To learn from it, run it again for longer.`,
    };
  }
  if (needs) {
    return {
      kind: 'none',
      heading: NO_MOVE_YET,
      waiting: true,
      reason:
        measure?.waitOnly === false
          ? `${needs} ${NO_TARGET_SET_NOTE}`
          : `${needs} Nothing to do until then.`,
    };
  }

  if (judgement.state !== 'evidence_only' && judgement.raise.eligible) {
    const from = input.verdict.evidence.dailyAmount;
    const to = judgement.raise.newDailyAmount;
    const title =
      from !== null && to !== null
        ? `Raise the amount from ${formatCentAmount(from, currencyCode)} to ${formatCentAmount(to, currencyCode)} a day`
        : 'Raise the amount';
    return {
      kind: 'raise',
      title,
      basis: judgement.raise.copy,
      unavailableReason: raiseUnavailableReason(provider),
    };
  }

  if (judgement.state === 'evidence_only') {
    const base = withoutNoChangePrefix(gateSentence(judgement, input.verdict.evidence));
    const decides =
      judgement.gate === 'not_created_by_mora'
        ? whatWouldDecideIt(input.verdict.evidence, currencyCode, {
            value: input.facts.current?.valueMajor ?? null,
            by,
          })
        : '';
    if (decides) {
      return {
        kind: 'none',
        heading: DECIDE_ON_YOUR_NUMBER,
        reason: decides.trim(),
        decision: true,
      };
    }
    return { kind: 'none', heading: NO_MOVE_YET, reason: base };
  }

  if (judgement.state === 'verdict' && measure) {
    if (measure.state === 'above_target') {
      if (input.rewrite)
        return rewriteMove(input.rewrite, input.verdict.evidence.target, currencyCode, by);
      return {
        kind: 'none',
        heading: NO_MOVE_YET,
        reason: `The cost is over your target. ${by} lists no ad of Mora's here to write a new opening line for, so there is nothing to press.`,
      };
    }
    if (measure.state === 'below_target' || measure.state === 'on_target') {
      const costTile = input.tiles?.tiles.find((tile) => tile.key === 'cost');
      const lower = costTile?.change?.direction === 'down';
      const where = measure.state === 'below_target' ? 'under' : 'close to';
      const limited = input.verdict.evidence.budgetLimited;
      return {
        kind: 'none',
        heading: NO_MOVE_YET,
        reason: `The cost is ${where} your target${lower ? ' and lower than the week before' : ''}. Mora will suggest a higher daily amount only if ${by} reports the amount is holding delivery back${limited === false ? '; it has not.' : `. Mora does not read that from ${by} yet.`}`,
      };
    }
  }

  const reason =
    judgement.raise.copy === NO_TARGET_COPY
      ? NO_TARGET_COPY
      : withoutNoChangePrefix(judgement.raise.copy);
  return { kind: 'none', heading: NO_MOVE_YET, reason };
}

export const NO_TARGET_HEADLINE_TAIL = ' so far, with no target to judge it against.';

/** "$738.59 a conversion so far, with no target to judge it against.", for a campaign with no plan to hold the cost against. */
function soFarCost(evidence: EvidenceInput, currencyCode: string | null): string | null {
  const { spend, results } = evidence;
  if (spend === null || results === null || results <= 0 || spend <= 0) return null;
  const noun = evidence.measureNoun ?? 'result';
  return `${formatCentAmount(spend / results, currencyCode)} a ${noun}${NO_TARGET_HEADLINE_TAIL}`;
}

/**
 * For a campaign Mora did not create, there is no plan to hold the cost against, so Mora cannot say the
 * cost is good or bad. It says what would decide it instead: what one result is worth to the person,
 * against the cost the channel reports, and how many results it takes before that cost settles.
 */
function whatWouldDecideIt(
  evidence: EvidenceInput,
  currencyCode: string | null,
  reportedValue: { value: number | null; by: string } | null = null,
): string {
  const { spend, results } = evidence;
  if (spend === null || results === null || results <= 0 || spend <= 0) return '';
  const noun = evidence.measureNoun ?? 'result';
  const floor = FLOOR_CONFIG.verdictResults.value;
  const value = reportedValue?.value ?? null;
  const worth =
    reportedValue && evidence.figuresAreOwnMeasure && value !== null && value > 0
      ? ` About ${formatWholeAmount(value / results, currencyCode || 'USD')} of value is counted for each one.`
      : '';
  const settle =
    results < floor
      ? `\nThe cost has not settled: Mora reads it once there are ${floor} ${noun}s.`
      : '';
  return ` Whether one ${noun} is worth more to you than it costs here.${worth}${settle}`;
}

/** The rewrite move exists only for Meta ads Mora drafted, so the result it names is Meta's own. */
const REWRITE_NOUN = RESULT_NOUN['meta-ads'];

/** "Line B costs $23.89 a purchase and line A $22.35. Both are over your $18.00 target, so this starts with the costlier ad; it does not say either is better." */
function rewriteMove(
  offer: RewriteOffer,
  target: number | null,
  currencyCode: string | null,
  by: string,
): NextMove {
  const { chosen, others, allOver } = offer;
  const targetText = target === null ? null : formatCentAmount(target, currencyCode);
  const costs = [chosen, ...others]
    .filter((ad) => ad.costText !== null)
    .map((ad, index) =>
      index === 0
        ? `${capitalize(ad.label)} costs ${ad.costText} a ${REWRITE_NOUN}`
        : `${ad.label} ${ad.costText}`,
    );
  const why =
    others.length === 0
      ? `${capitalize(chosen.label)} costs ${chosen.costText ?? 'more than it counts'} a ${REWRITE_NOUN}${targetText ? `, over your ${targetText} target` : ''}.`
      : `${costs.length === 2 ? `${costs[0]} and ${costs[1]}.` : `${costs.join(', ')}.`}${allOver && targetText ? ` ${others.length === 1 ? 'Both are' : 'All are'} over your ${targetText} target, so this starts with the costlier ad; it does not say ${others.length === 1 ? 'either' : 'any'} is better.` : ' This starts with the costlier ad; it does not say any is better.'}`;
  return {
    kind: 'rewrite',
    title: `Write a new opening line for ${chosen.label}`,
    basis: `${why} Mora saves the new line as a draft beside the running ad and gives you the text to paste into ${by}. The running ad is not changed.`,
    sub: `Nothing is sent to ${by}.`,
    adKey: chosen.key,
    adName: chosen.name,
  };
}

export interface ProgressRowFacts {
  label: string;
  have: number;
  need: number;
}

/**
 * What a campaign below the floors still lacks, as two rows of "have of need": the results counted and
 * the days delivered. The floors are read here and nowhere else on the page. A thin measure (bookings)
 * has no results row, because its verdict waits on days and a target, not on a count. A count that was
 * not read is left out, never drawn as zero.
 */
export function buildProgressRows(input: {
  provider: DetailProvider;
  measure: Pick<CampaignMeasure, 'state'> | null;
  evidence: Pick<EvidenceInput, 'results' | 'daysDelivered'>;
  outcome: string | null;
}): ProgressRowFacts[] | null {
  if (input.measure?.state !== 'too_early') return null;
  const thin = isThinOutcome(input.outcome);
  const rows: ProgressRowFacts[] = [];
  if (!thin && input.evidence.results !== null) {
    rows.push({
      label: `${input.provider === 'meta-ads' ? 'Purchases' : 'Conversions'} counted`,
      have: input.evidence.results,
      need: FLOOR_CONFIG.verdictResults.value,
    });
  }
  if (input.evidence.daysDelivered !== null) {
    const need = thin ? FLOOR_CONFIG.thinVerdictDays.value : FLOOR_CONFIG.verdictDays.value;
    rows.push({
      label: 'Days delivered',
      have: Math.min(input.evidence.daysDelivered, need),
      need,
    });
  }
  return rows;
}
